import * as Yup from 'yup';
import PropTypes from 'prop-types';
import React from 'react';
import { useBoolean } from 'usehooks-ts';
import { useFormik } from 'formik';
import { useLocation, Link, useHistory } from 'react-router-dom';

import Breadcrumb from 'components/App/Breadcrumb';
import Button from 'components/App/Button';
import Card from 'components/App/Card';
import FormCreatePromotion from 'components/FormCreatePromotion';
import ModalPromotionSummarize from 'components/Modals/ModalPromotionSummarize';

import DATES_OF_WEEK from 'constants/datesOfWeek';
import INITIAL_VALUE from 'constants/initialValue';
import { LOCATIONS, LOGIN_TYPE, storageKey, themeColor } from 'constants/index';
import { PROMOTION_STATUS, PROMOTION_TYPE } from 'constants/promotion';
import cn from 'utils/classnames';
import { createLongtermPromotionValidation, createPromotionValidation } from 'utils/validate';

import { ReactComponent as LongTermPromotionIcon } from 'assets/images/long-term-promotion.svg';

import styles from './CreateLongTermPromotion.module.scss';

// BREADCRUMB constant moved inside component dynamically

function CreateLongTermPromotion(props) {
  const {
    isLoadingCreatePromotion,

    roomOfPlaceList,
    placePriceTypes,
    promotionDetail,

    actionGetPromotionDetail,
    actionGetListRoomsOfPlace,
    actionGetPlacePriceTypes,
    actionPostPromotion,
    actionEditPromotion,
    actionClearDataPromotionDetail,
  } = props;

  const history = useHistory();
  const location = useLocation();
  const placeId = new URLSearchParams(location.search).get('place');
  const promotionId = new URLSearchParams(location.search).get('promotion');

  const isExtranet = React.useMemo(() =>
    localStorage.getItem(storageKey.LOGIN_TYPE) === LOGIN_TYPE.EXTRANET,
  []);

  const breadcrumbList = React.useMemo(() => [
    {
      name: 'Chương trình khuyến mãi',
      isActive: false,
      link: '',
    },
    {
      name: 'Tạo chương trình khuyến mãi',
      isActive: false,
      link: placeId
        ? `${LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION}?place=${placeId}`
        : LOCATIONS.LOCATION_DETAIL.CREATE_PROMOTION,
    },
    {
      name: 'Ưu Đãi Dài Hạn',
      isActive: true,
      link: '',
    },
  ], [placeId]);

  const {
    value: isOpenModal,
    setTrue: onOpenModal,
    setFalse: onCloseModal,
  } = useBoolean(false);

  const validation = useFormik({
    initialValues: {
      rooms: [],
      isAllRooms: true,
      isAllPriceTypes: true,
      applyBookingDays: DATES_OF_WEEK,
      priceTypes: [],
      bookingStartAt: '',
      bookingEndAt: '',
      stayAt: [{
        _id: '1',
        startAt: '',
        endAt: '',
      }],
      discountRate: '',
      discountType: PROMOTION_TYPE.LONG_TERM,
      name: '',
      minStayNights: '1',
    },

    validationSchema: Yup.object({
      rooms: createPromotionValidation.rooms,
      priceTypes: createPromotionValidation.priceTypes,
      applyBookingDays: createPromotionValidation.applyBookingDays,
      discountRate: createPromotionValidation.discountRate,
      bookingStartAt: createPromotionValidation.bookingStartAt,
      bookingEndAt: createPromotionValidation.bookingEndAt,
      stayAt: createPromotionValidation.stayAt,
      name: createPromotionValidation.name,
      minStayNights: createLongtermPromotionValidation.minStayNights,
    }),

    onSubmit: () => {
      onOpenModal();
    },
  });

  const onCallback = React.useCallback(() => {
    onCloseModal();
    history.push({
      pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
      search: `?place=${placeId}&status=PENDING`,
    });
  }, [placeId, promotionId]);

  const onSubmitCreatePromotion = React.useCallback(() => {
    const newPromotion = {
      ...validation.values,
      applyBookingDays: validation.values.applyBookingDays
        .filter((item) => item.isChecked)
        .map((item) => item.value),
      stayAt: validation.values.stayAt.map((item) => ({
        startAt: item.startAt,
        endAt: item.endAt,
      })),
    };

    if (promotionId) {
      actionEditPromotion({
        placeId,
        promotionId,
        promotion: newPromotion,
        callback: onCallback,
      });
    } else {
      actionPostPromotion({
        placeId,
        promotion: newPromotion,
        callback: onCallback,
      });
    }
  }, [validation.values, placeId]);

  React.useEffect(() => {
    if (placeId) {
      actionGetListRoomsOfPlace({ placeId });
      actionGetPlacePriceTypes({ placeId });
    }
  }, [placeId]);

  React.useEffect(() => {
    if (promotionId && placeId) {
      actionGetPromotionDetail({ placeId, promotionId });
    }

    return () => {
      actionClearDataPromotionDetail();
    };
  }, [promotionId, placeId]);

  React.useEffect(() => {
    if (Object.keys(promotionDetail).length > 0) {
      validation.setValues((values) => ({
        ...values,
        ...promotionDetail,
        discountRate: promotionDetail.discount,
        applyBookingDays: promotionDetail.applyBookingDays ? DATES_OF_WEEK.map((item) => ({
          ...item,
          isChecked: promotionDetail.applyBookingDays.includes(item.value),
        })) : DATES_OF_WEEK,
      }));
    }

    return () => {
      validation.resetForm();
    };
  }, [promotionDetail]);

  return (
    <div className={cn('page-content', styles.Page)}>
      <Breadcrumb
        pageTitle="Tạo ưu đãi dài hạn"
        breadcrumbList={breadcrumbList}
      />

      <div className={styles.Wrapper}>
        <Card className={styles.Section}>
          <div className={styles.SectionTop}>
            <LongTermPromotionIcon className={styles.Icon} />

            <div className={styles.SectionTopTitle}>
              <h3>Tạo Ưu Đãi Dài Hạn</h3>
              <p>
                Tùy chỉnh khuyến mãi này theo nhu cầu của Quý vị.
                Chọn ngày, mức giảm giá, đối tượng áp dụng và hơn thế nữa.
              </p>
            </div>
          </div>
        </Card>

        <FormCreatePromotion
          isLongTerm
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
        />

        <div className={isExtranet ? styles.StickyButtonsExtranet : styles.StickyButtons}>
          <div className={styles.ButtonWrap}>
            <Link to={{
              pathname: LOCATIONS.LOCATION_DETAIL.PROMOTION,
              search: `?place=${placeId}`,
            }}
            >
              <Button
                color={themeColor.SECONDARY}
              >
                Hủy
              </Button>
            </Link>

            <Button
              onClick={validation.handleSubmit}
              disabled={promotionDetail.discountStatus
                && promotionDetail.discountStatus !== PROMOTION_STATUS.PENDING}
            >
              {promotionId ? 'Cập nhật' : 'Tạo'}
            </Button>
          </div>
        </div>

        <ModalPromotionSummarize
          isOpen={isOpenModal}
          loading={isLoadingCreatePromotion}
          validation={validation}
          roomOfPlaceList={roomOfPlaceList}
          placePriceTypes={placePriceTypes}
          onCloseClick={onCloseModal}
          onSubmit={onSubmitCreatePromotion}
        />
      </div>
    </div>
  );
}

CreateLongTermPromotion.propTypes = {
  isLoadingCreatePromotion: PropTypes.bool.isRequired,

  roomOfPlaceList: PropTypes.instanceOf(Array),
  placePriceTypes: PropTypes.instanceOf(Array),
  promotionDetail: PropTypes.instanceOf(Object),

  actionGetListRoomsOfPlace: PropTypes.func.isRequired,
  actionGetPlacePriceTypes: PropTypes.func.isRequired,
  actionPostPromotion: PropTypes.func.isRequired,
  actionGetPromotionDetail: PropTypes.func.isRequired,
  actionEditPromotion: PropTypes.func.isRequired,
  actionClearDataPromotionDetail: PropTypes.func.isRequired,
};

CreateLongTermPromotion.defaultProps = {
  roomOfPlaceList: INITIAL_VALUE.ARRAY,
  placePriceTypes: INITIAL_VALUE.ARRAY,
  promotionDetail: INITIAL_VALUE.OBJECT,
};

export default CreateLongTermPromotion;
